import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { AppDialogService } from './app-dialog.service';
import { LogService } from './log.service';
import { AlarmService } from './alarm.service';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { TranslateService } from '@ngx-translate/core';
import { NewLocationFormComponent } from '../components/new-location-form/new-location-form.component';
import { AlarmTypes } from '@components/alarm-email-form/alarm-email-form.component';
import { DropDown, MultiDropDown } from '../interfaces/drop-down.interface';
import { IDeliveryEfficiencyForm } from '../components/delivery-efficiency-forecasting/IDeliveryEfficiencyForm';
import { IEmailAlertFormResponse } from '@interfaces/email-alerts-form';
import { CacheBuster, Cacheable, globalCacheBusterNotifier } from 'ts-cacheable';
import { environment } from '../../environments/environment';
import { ILocationForm } from '@interfaces/location-form.interface';
import { LocationGridRequest, GridResult, LocationGridResult } from '../page/location/models';
import { TtlRequestCache } from '@utils/ttl-request-cache';

@Injectable()
export class LocationService {
  private readonly apiURL: string = 'Location';
  private readonly popUpWidth: string = '85%';
  private readonly savingMessage = 'Saving...';

  // Backs getLocationDetails() below - see the comment there for why this exists and why it is
  // not @Cacheable. Bound to globalCacheBusterNotifier because the response is CSO-stamped; see
  // the note on the notifier in getLocationDetails().
  private readonly locationDetailsCache = new TtlRequestCache<number, ILocationDetailsObject>(
    5000,
    globalCacheBusterNotifier,
  );

  constructor(
    private logger: LogService,
    private restClient: DSRESTClient,
    private httpClient: HttpClient,
    private dialog: MatDialog,
    private translate: TranslateService,
    private _ads: AppDialogService,
    private _as: AlarmService,
    public snackBar: MatSnackBar,
  ) {}

  // Short-lived cache: LocationNavigationGuard fetches this to authorize the route, the
  // page-title resolver fetches it to label the tab, and LocationDetailsComponent fetches it
  // again milliseconds later to render. The window collapses those into one request without
  // holding details long enough to go stale.
  //
  // Hand-rolled instead of @Cacheable so the cache is inspectable and clearable from code -
  // shareReplay keeps concurrent callers on one in-flight/most-recent request, and
  // TtlRequestCache's sliding expiry reproduces the maxAge/slidingExpiration the decorator gave.
  //
  // The cache is bound to globalCacheBusterNotifier, and must stay bound. CanEdit and the
  // organization dropdown are stamped from the caller's CSO, and LocationNavigationGuard switches
  // the CSO between its own read and the component's - so without the bust the page renders
  // against the organization the user just left: form disabled, actions hidden until a reload.
  // updateLastSelectedOrganization and saveLocationDetails both fire the notifier.
  getLocationDetails(locId: number): Observable<ILocationDetailsObject> {
    return this.locationDetailsCache.get(locId, () =>
      this.restClient.getDirect<ILocationDetailsObject>(
        `${this.apiURL}/GetLocationDetails/${locId}`,
      ).pipe(
        tap((response) => this.logger.log('Location/GetLocationDetails Response', response)),
      ),
    );
  }

  getLocationLatLon(locId: number): Observable<ILatLng> {
    const apiResponse = this.restClient.getDirect<ILatLng>(
      `${this.apiURL}/GetLocationLatLon/${locId}`,
    );
    return apiResponse.pipe(
      tap({
        next: (response) => {
        },
        error: (response: any) => {
          this._ads.showErrorMessage(this.translate.instant('LocationForm.MissingLatLng'));
        }
      }),
    );
  }

  @CacheBuster({
    isInstant: true,
    cacheBusterNotifier: globalCacheBusterNotifier,
  })
  saveLocationDetails(loc: ILocationForm): Observable<any> {
    const apiResponse = this.restClient.post(
      `${this.apiURL}/SaveLocationDetails`,
      loc,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Location/SaveLocationDetails Response', response),
      ),
    );
  }
  saveBulkLocationDetails(loc: any[]): Observable<any> {
    const apiResponse = this.restClient.post(
      `${this.apiURL}/SaveBulkLocationDetails`,
      loc,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Location/SaveBulkLocationDetails Response', response),
      ),
    );

  }
  saveNewLocation(value: INewLocation): Observable<any> {
    const payload: INewLocation = {
      ...value,
      daylightSavings: value.daylightSavings ?? false,
      active: value.active ?? true,
      isShipTo: value.isShipTo ?? false,
    };

    // The API DTO currently uses non-nullable int for PrimaryContactId.
    // Do not send null for optional modal selection.
    if (payload.primaryContactId == null) {
      delete payload.primaryContactId;
    }

    return this.restClient
      .postForResponse<number>(`${this.apiURL}/SaveLocation`, payload)
      .pipe(
        tap({
          next: (response) => {
            this._ads.showSuccessMessage(this.translate.instant('LocationForm.LocationAddedSuccessfully'));
            this.logger.log(`${this.apiURL}/SaveLocation`, response);
          },
          error: (error) => {
            this._ads.showErrorMessage(this.translate.instant('LocationForm.ErrorAddingLocation'));
            this.logger.error(`${this.apiURL}/SaveLocation`, error);
          },
        }),
      );
  }

  getOrgLocationsWithAnomalies(params: any): Observable<any> {
    let url = `${this.apiURL}/GetOrgLocationsWithAnomalies/${params?.orgId}`;
    return this.restClient.getDirect(url).pipe(
      tap((response) => {
        this.logger.log(url, response);
      }),
    );
  }

  getLocationDropDownListByOrgId(
    orgId: number,
    includeChildOrgs: boolean = true,
  ): Observable<DropDown<number>[]> {
    return of(orgId).pipe(
      filter((orgId) => orgId != null),
      switchMap((orgId) => {
          let url = `${this.apiURL}/GetLocationDropDownListByOrgId/${orgId}?includeChildOrgs=${includeChildOrgs}`;
          return this.restClient.getDirect<DropDown<number>[]>(url).pipe(
            tap((response) => {
              this.logger.log(url, response);
            }),
          );
      }),
    );
  }

  @Cacheable({maxAge: 5000, slidingExpiration: true })
  locationNameAlreadyExistsForOrg(
    organizationId: number,
    locationName: string,
    excludeLocationId?: number,
    includeChildOrgs: boolean = true,
  ): Observable<boolean> {
    const params = new URLSearchParams();
    params.set('organizationId', String(organizationId));
    params.set('locationName', locationName);
    params.set('includeChildOrgs', String(includeChildOrgs));
    if (excludeLocationId != null && excludeLocationId > 0) {
      params.set('excludeLocationId', String(excludeLocationId));
    }
    const url = `${this.apiURL}/LocationNameAlreadyExistsForOrg?${params.toString()}`;
    return this.restClient.getDirect<boolean>(url).pipe(
      tap((response) => this.logger.log(url, response)),
    );
  }

getLocationsForDropDownByOrgIds(
  orgIds: number[],
  includeChildOrgs: boolean = true
  ): Observable<any> {
     if (!Array.isArray(orgIds) || orgIds.length === 0) return of([]);

    const url = `${this.apiURL}/get-locations-by-orgIds?includeChildOrgs=${includeChildOrgs}`;
    return this.restClient.postDirect<any>(url, orgIds).pipe(
      tap((response) => {
        this.logger.log(url, response);
      })
    );
  }




  showAddNewLocationDialog(
    organizationList: any,
    selectedOrgId: number | null,
  ): Observable<any> {
    return this.dialog
      .open(NewLocationFormComponent, {
        width: this.popUpWidth,
        panelClass: 'cso-dialog',
        disableClose: true,
        data: {
          organizationList: organizationList,
          selectedOrgId: selectedOrgId,
        },
      })
      .afterClosed();
  }

  getStates(countryId: string): Observable<object> {
    return this.getCountries().pipe(
      map((countries: any[]) => {
        return countries.find((country) => country?.name === countryId);
      }),
      distinctUntilChanged(),
    );
  }

  getCountries(): Observable<any> {
    let url = `assets/countries+states.json`;
    return this.httpClient.get<object>(url).pipe(
      tap((response) => {
        this.logger.log(url, response);
      }),
    );
  }

  getTimeZones(): Observable<any> {
    let url = `${this.apiURL}/timezones`;
    return this.restClient.getDirect(url).pipe(
      tap((response) => {
        this.logger.log(url, response);
      }),
    );
  }

  getDeliveryEfficiency(locId: number): Observable<IDeliveryEfficiencyForm> {
    const url = `${this.apiURL}/${locId}/delivery-efficiency`;
    return this.restClient
      .getAsModel<IDeliveryEfficiencyForm>(
        url
      );
  }

  setDeliveryEfficiency(
    locId: number,
    model: IDeliveryEfficiencyForm,
  ): Observable<void> {
    const url = `${this.apiURL}/${locId}/delivery-efficiency`;
    this._ads.showGeneralMessage(this.savingMessage);
    return this.restClient
      .post(
        url, model
      )
      .pipe(tap(this.tapSnackBarMessages));
  }

  saveAlarmDefinitions(locationId: number, alarms: any[]): Observable<object> {
    alarms.map((alarm) => {
      alarm.alarmType = AlarmTypes.Location;
      alarm.entityId = locationId;
    });
    return this._as.saveAlarmDefinitions(alarms)
    .pipe(tap(this.tapSnackBarMessages));
  }

  getAlarmDefinitions(locationId: number): Observable<IEmailAlertFormResponse> {
    return this._as.getAlarmDefinitions(AlarmTypes.Location, locationId);
  }

  getLocations(filter: LocationGridRequest): Observable<GridResult<LocationGridResult>> {
    return this.restClient.postForResponse<GridResult<LocationGridResult>>(
      `${this.apiURL}/Grid`,
      filter,
    );
  }

  exportLocationsToExcel(filter: LocationGridRequest): Observable<Blob> {
    return this.restClient.postFile(`${this.apiURL}/exporttoexcel`, filter);
  }


  private readonly tapSnackBarMessages = {
    next: (response) => {
      this._ads.showSuccessMessage('Saved Successfully');
    },
    error: (error) => {
      this._ads.showErrorMessage('Save Failed');
    },
  };

  private showErrorMessage(message: string, response: any) {
    this.logger.log('Organization Error: ' + message, response);
    let duration = environment.snackbarDelayBeforeClose;
    let errors = [];

    if (response.error && response.error.constructor === Array) {
      errors = response.error;
    } else if (
      response.error &&
      response.error.errors &&
      response.error.errors.constructor === Array
    ) {
      errors = response.error.errors;
    }

    if (errors.length > 0) {
      const timestamperrorsExist = response.error.find(
        (error: any) => error.errorNo > 0 && error.errorNo < 4,
      );

      if (timestamperrorsExist) {
        duration = environment.snackbarDelayBeforeClose * 2;
        message += `\n ${this.translate.instant('General.TimestampError')}`;
      } else {
        response.error.forEach((error: any) => {
          message += `\n ${this.translate.instant(
            'OrganizationDetails.Error.' + error.stringResourceKey,
          )}`;
        });
      }
    }

    this.snackBar.open(message, null, {
      duration: duration,
      panelClass: ['snackbar-failure'],
    });
  }
}

export interface ILocationDetailsObject {
  details: ILocationDetails;
  tankCount: number;
  organizationDropdown: any[];
  primaryContactDropdown: any[];
  errorCode: number;
  /**
   * False when the location is only reachable through a cross-CSO SupplyNet grant. The server
   * owns this decision - do not re-derive it from organizationId.
   */
  canEdit: boolean;
  /**
   * True when the location's organization is the active CSO or sits under it in the org tree.
   * The guard uses it to decide whether opening the location has to move the CSO - do not
   * re-derive it by comparing organizationId to the CSO, that misses child orgs.
   */
  isInCsoScope: boolean;
}

export interface ILocationDetails {
  locationId: number;
  organizationId: number;
  locationName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  primaryContactId: number;
  locationGuid: any;
  userLocNumber: string;
  isStockLocation: boolean;
  isShipTo: boolean;
  active: boolean;
  timeZoneId: number;
  locationMatchText: string;
  ownerLocationId: number;
  /**
   * CommPktMgr's mirror-location flag. Display only - the portal never writes it.
   */
  isAffiliatedLocation: boolean;
  organizationIdPlusName: string;
  organizationName: string;
  latlonTypeId: number;
  mapData: ILocationDetailsMapData;
}

export interface ILocationDetailsMapData {
  addressBasedLatLon: ILatLonItem;
  mapPinBasedLatLon: ILatLonItem;
  activeLatLonTypeId: number;
}

export interface ILatLonItem {
  latLonTypeId: number;
  address: string;
  latitude: number;
  longitude: number;
  isDirty: boolean;
}

export interface ILatLng {
  latitude: number;
  longitude: number;
}

export interface INewLocation {
  organizationId: number;
  locationName: string;
  userLocationNumber?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode?: string;
  country: string;
  lat?: number;
  lon?: number;
  active?: boolean;
  locationMatchText?: string;
  timeZoneId: number;
  daylightSavings?: boolean;
  primaryContactId?: number | null;
  notes?: string;
  isShipTo?: boolean;
}
